import { Alert, Button, Space } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUpdateEmailFromTokenMutation } from '../../../api/mutations/updateEmailFromToken';
import LoadingElement from '../../../components/LoadingElement';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';

type UpdateEmailValues = { token: string; email: string };

type UpdateEmailProps = {
    token: string;
};

const UpdateEmail = ({ token }: UpdateEmailProps) => {
    const { t } = useTranslation(['signInPage']);
    const navigate = useNavigate();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [mutation] = useUpdateEmailFromTokenMutation();
    const locationState = useLocation().state as { token?: string; email?: string; userId?: string };

    const updateEmailFromToken = useCallback(
        async ({ token, email }: UpdateEmailValues) => {
            const { data } = await mutation({ variables: { token, email } });
            setLoading(false);
            setIsSubmitted(data.updateEmailFromToken);
        },
        [mutation]
    );

    useEffect(() => {
        const { email } = locationState;
        updateEmailFromToken({ token, email });
    }, [updateEmailFromToken, token, locationState]);

    const element = isSubmitted ? (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Alert description={t('signInPage:updateEmail.successMessage')} type="success" showIcon />
            <Button
                htmlType="button"
                onClick={() => {
                    navigate('../signIn', { state: { noWebAuthn: true } });
                }}
                type="dashed"
                block
            >
                {t('signInPage:updateEmail.signIn')}
            </Button>
        </Space>
    ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Alert description={t('signInPage:updateEmail.failMessage')} type="error" showIcon />
        </Space>
    );

    if (loading) {
        return <LoadingElement />;
    }

    return <LoginContent title={t('signInPage:updateEmail.title')}>{element}</LoginContent>;
};

export default UpdateEmail;
